/* eslint-disable @typescript-eslint/no-explicit-any */
import { EActivityType } from '@constants';
import { IActivity } from '@sdk/contracts';
import { Button, Checkbox, Divider, Drawer, Form, Input, message, Switch } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InternalUserSelector } from '../Form';

import {
  LockActivityWrapper,
  LockActivityLabel,
  ActivityTypeWrapper,
  NameAndDescriptionWrapper,
  ButtonsWrapper
} from './ActivityForm.styles';
import { ActivityStudyTime } from './ActivityStudyTime';
import { ActivityTypes } from './ActivityTypes';
import { ActivityTypeSelector } from './ActivityTypeSelector';
import { Attachments } from './Attachments';
import { DeleteActivity } from './DeleteActivity';
import { Task } from './Task';

import { useActivityForm, useMutateActivity, useProfile } from '@/store';
import { areObjectEquals } from '@/utils';

const DRAWER_WIDTH = 500;

export const ActivityForm = () => {
  const [form] = Form.useForm();
  const [hasChanges, setHasChanges] = useState(false);
  const {
    roleCheck: { isTeacher, isManager }
  } = useProfile();
  const {
    data,
    course,
    isOpen,
    sectionId,
    setIsOpen,
    setData,
    setCourse,
    setSectionId,
    setHasVideoCheckPoint,
    hasVideoCheckPoint,
    generatedVideoCheckpoints
  } = useActivityForm();
  const [type, setType] = useState<EActivityType | null>();
  const { createActivity, updateActivity, isWorking } = useMutateActivity();
  const { t } = useTranslation(['CourseTranslations', 'DefaultTranslations']);

  const DRAWER_TITLE = t(
    data ? 'CourseTranslations:EDIT_ACTIVITY' : 'CourseTranslations:NEW_ACTIVITY'
  );

  const isInitialLoad = useRef(true);

  useEffect(() => {
    if (!isInitialLoad.current) {
      setHasChanges(true);
    }
  }, [hasVideoCheckPoint]);

  useEffect(() => {
    form.setFieldValue('questions', generatedVideoCheckpoints);

    if (generatedVideoCheckpoints && generatedVideoCheckpoints.length > 0) {
      setHasChanges(true);
    }
  }, [form, generatedVideoCheckpoints]);

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        ...data,
        task: data.task && { ...data.task, deadline: dayjs(data.task.deadline) }
      });
      setType(data.type);
      if (data.questions && data.questions.length > 0) {
        setHasVideoCheckPoint(true);
      }
      setHasChanges(false);
      requestAnimationFrame(() => {
        isInitialLoad.current = false;
      });
    }
  }, [data, sectionId, course]);
  if (!sectionId) return null;

  if (!course) return null;

  const onSuccess = () => {
    setHasChanges(false);
    handleClose(true);
  };

  const handleFinish = (values: IActivity) => {
    if (!values.task && !values.resource) {
      message.error(t('CourseTranslations:ACTIVITY_RESOURCE_REQUIRED'));
      return;
    }

    const allQuestionsAnswered = values.questions?.every(
      question => question?.correctAnswer !== undefined
    );

    if (values.questions && !allQuestionsAnswered) {
      message.error(t('CourseTranslations:ALL_QUESTIONS_MUST_BE_ANSWERED'));
      return;
    }

    const taskTitle = values.task?.title ?? values.name;
    const taskDescription = values.task?.description ?? values.description;
    const task = values.task
      ? { ...values.task, title: taskTitle, description: taskDescription, course: course._id }
      : undefined;

    const estimatedStudyTime =
      values.estimatedStudyTime?.hours || values.estimatedStudyTime?.minutes
        ? {
            hours: values.estimatedStudyTime?.hours ?? 0,
            minutes: values.estimatedStudyTime?.minutes ?? 0
          }
        : undefined;

    if (values._id) {
      const updateValues = !hasVideoCheckPoint ? { ...values, questions: [] } : { ...values };
      updateActivity(
        {
          courseId: course._id,
          sectionId,
          courseName: course.name,
          activity: { ...updateValues, task, estimatedStudyTime }
        },
        {
          onSuccess: () => {
            onSuccess();
          }
        }
      );
    } else {
      createActivity(
        {
          courseName: course.name,
          sectionId,
          activity: { ...values, task, estimatedStudyTime },
          courseId: course._id
        },
        {
          onSuccess: () => {
            onSuccess();
          }
        }
      );
    }
  };

  const handleFieldsChange = (changedValues: any) => {
    const name = changedValues[0].name[0];
    const value = changedValues[0].value;
    if (name === 'type') setType(value);
    const areEquals = areObjectEquals(data, form.getFieldsValue());
    setHasChanges(!areEquals);
  };

  const handleClose = (forceClose?: boolean) => {
    if (hasChanges && !forceClose) {
      message.warning(t('CourseTranslations:UNSAVED_CHANGES'));
      return;
    }
    isInitialLoad.current = true;
    setHasVideoCheckPoint(false);
    setData(null);
    setType(null);
    setCourse();
    setSectionId();
    setIsOpen(false);
    form.resetFields();
  };

  const resetStudyTime = () => {
    form.setFieldsValue({
      estimatedStudyTime: { hours: undefined, minutes: undefined }
    });
    setHasChanges(true);
  };

  return (
    <Drawer
      open={isOpen}
      destroyOnHidden
      width={DRAWER_WIDTH}
      title={DRAWER_TITLE}
      footer={
        <ButtonsWrapper>
          <Button onClick={() => handleClose(true)} type="default">
            {t('DefaultTranslations:CANCEL')}
          </Button>
          <Button disabled={!hasChanges} loading={isWorking} onClick={form.submit} type="primary">
            {t('DefaultTranslations:SAVE')}
          </Button>
        </ButtonsWrapper>
      }
      onClose={() => handleClose()}>
      <Form
        layout="vertical"
        form={form}
        onFinish={handleFinish}
        onFieldsChange={handleFieldsChange}>
        <Form.Item name="_id" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="__v" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="task" hidden={!isManager}>
          <Task courseId={course._id} />
        </Form.Item>
        <Form.Item name="active" valuePropName="checked" hidden>
          <Checkbox />
        </Form.Item>

        <NameAndDescriptionWrapper>
          <Form.Item
            name="name"
            rules={[{ required: true, message: t('DefaultTranslations:FIELD_REQUIRED') }]}
            label={t('CourseTranslations:NAME')}>
            <Input placeholder={t('CourseTranslations:ACTIVITY_NAME')} type="text" />
          </Form.Item>

          <Form.Item
            name="description"
            rules={[{ required: true, message: t('DefaultTranslations:FIELD_REQUIRED') }]}
            label={t('CourseTranslations:DESCRIPTION')}>
            <Input.TextArea placeholder={t('CourseTranslations:ACTIVITY_DESCRIPTION')} rows={5} />
          </Form.Item>
        </NameAndDescriptionWrapper>

        <LockActivityWrapper>
          <Form.Item name="isLocked" layout="horizontal">
            <Switch />
          </Form.Item>
          <LockActivityLabel>{t('CourseTranslations:LOCK_ACTIVITY')}</LockActivityLabel>
        </LockActivityWrapper>

        <ActivityTypeWrapper>
          <Form.Item
            name="type"
            rules={[{ required: true, message: t('CourseTranslations:REQUIRED_ACTIVITY_TYPE') }]}
            label={t('CourseTranslations:SELECT_ACTIVITY_TYPE')}>
            <ActivityTypeSelector />
          </Form.Item>
        </ActivityTypeWrapper>

        {type && (
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.resource !== currentValues.resource ||
              prevValues.task?.resource !== currentValues.task?.resource ||
              prevValues.type !== currentValues.type
            }>
            {({ getFieldValue }) => {
              let fieldName: string | string[];

              if (isTeacher) {
                fieldName = ['task', 'resource'];
              } else {
                const resource = getFieldValue('resource');
                fieldName = resource ? 'resource' : ['task', 'resource'];
              }

              return (
                <Form.Item
                  name={fieldName}
                  labelCol={{ span: 24 }}
                  label={t(`CourseTranslations:${type}_SELECTOR_LABEL`)}>
                  <ActivityTypes type={type} />
                </Form.Item>
              );
            }}
          </Form.Item>
        )}

        {type && (
          <>
            <Divider />
            <ActivityStudyTime type={type} resetStudyTime={resetStudyTime} />
          </>
        )}

        <Divider />

        <Form.Item name="attachments">
          <Attachments />
        </Form.Item>

        {isManager && (
          <Form.Item name="createdBy" label={t('CourseTranslations:CREATED_BY')}>
            <InternalUserSelector />
          </Form.Item>
        )}

        <DeleteActivity handleClose={handleClose} sectionId={sectionId} />
      </Form>
    </Drawer>
  );
};
